import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { apiClient } from '@edgerunner/shared';

interface AuthUser {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  is_active: boolean;
  is_superuser: boolean;
  pii_phi_eligible: boolean;
  is_verified: boolean;
}

interface AuthStore {
  // State
  user: AuthUser | null;
  isAuthenticated: boolean;
  isSessionChecked: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  checkSession: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, firstName?: string, lastName?: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

/** Map backend error codes/strings to user-friendly messages */
function mapErrorMessage(message: string): string {
  if (message.includes('REGISTER_USER_ALREADY_EXISTS')) {
    return 'An account with this email already exists.';
  }
  if (message.includes('LOGIN_BAD_CREDENTIALS')) {
    return 'Invalid email or password.';
  }
  if (message.includes('LOGIN_USER_NOT_VERIFIED')) {
    return 'Please verify your email before logging in.';
  }
  if (message.includes('REGISTER_INVALID_PASSWORD')) {
    return 'Password does not meet requirements. Use at least 8 characters.';
  }
  return message;
}

export const useAuthStore = create<AuthStore>()(
  devtools(
    (set) => ({
      user: null,
      isAuthenticated: false,
      isSessionChecked: false,
      isLoading: false,
      error: null,

      checkSession: async () => {
        set({ isLoading: true }, false, 'auth/checkSession');
        try {
          const user = await apiClient.auth.getCurrentUser();
          set({ user, isAuthenticated: true, isSessionChecked: true, isLoading: false }, false, 'auth/checkSession(success)');
        } catch {
          set({ user: null, isAuthenticated: false, isSessionChecked: true, isLoading: false }, false, 'auth/checkSession(fail)');
        }
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null }, false, 'auth/login');
        try {
          await apiClient.auth.login(email, password);
          const user = await apiClient.auth.getCurrentUser();
          set({ user, isAuthenticated: true, isLoading: false }, false, 'auth/login(success)');
        } catch (err: any) {
          set({ isLoading: false, error: mapErrorMessage(err.message || 'Login failed') }, false, 'auth/login(fail)');
        }
      },

      register: async (email: string, password: string, firstName?: string, lastName?: string) => {
        set({ isLoading: true, error: null }, false, 'auth/register');
        try {
          await apiClient.auth.register(email, password, firstName, lastName);
          // Auto-login after registration
          await apiClient.auth.login(email, password);
          const user = await apiClient.auth.getCurrentUser();
          set({ user, isAuthenticated: true, isLoading: false }, false, 'auth/register(success)');
        } catch (err: any) {
          set({ isLoading: false, error: mapErrorMessage(err.message || 'Registration failed') }, false, 'auth/register(fail)');
        }
      },

      logout: async () => {
        try {
          await apiClient.auth.logout();
        } catch {
          // Logout best-effort
        }
        set({ user: null, isAuthenticated: false, error: null }, false, 'auth/logout');
      },

      clearError: () => set({ error: null }, false, 'auth/clearError'),
    }),
    { name: 'useAuthStore' },
  ),
);
